import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FiArrowLeft, FiSave, FiImage, FiX } from 'react-icons/fi';
import { toast } from 'react-hot-toast';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import articleService from '../../services/articleService';
import { UpdateArticleData } from '../../types/article.types';
import Button from '../../components/common/Button/Button';
import Input from '../../components/common/Input/Input';
import Card from '../../components/common/Card/Card';
import Loader from '../../components/common/Loader/Loader';
import styles from './CreateArticle.module.css';

interface FormData {
  headline: string;
  excerpt: string;
  description: string;
  category: string;
  tags: string;
  isFeatured: boolean;
  isBreakingNews: boolean;
  status: 'draft' | 'published';
  metaTitle: string;
  metaDescription: string;
  featuredImage: File | null;
}

const EditArticle: React.FC = () => {
  const { id: slug } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [imagePreview, setImagePreview] = useState<string>('');
  const [originalData, setOriginalData] = useState<FormData | null>(null);
  const [formData, setFormData] = useState<FormData>({
    headline: '',
    excerpt: '',
    description: '',
    category: 'tech',
    tags: '',
    isFeatured: false,
    isBreakingNews: false,
    status: 'draft',
    metaTitle: '',
    metaDescription: '',
    featuredImage: null,
  });

  useEffect(() => {
    if (slug) {
      fetchArticle(slug);
    } else {
      toast.error('Invalid article URL');
      navigate('/top-news');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  const fetchArticle = async (articleSlug: string) => {
    try {
      setLoading(true);
      console.log('📖 Fetching article for edit with slug:', articleSlug);

      const article = await articleService.getArticleBySlug(articleSlug);
      console.log('✅ Article loaded for edit:', article.headline);

      const initialData: FormData = {
        headline: article.headline,
        excerpt: article.excerpt || '',
        description: article.description,
        category: article.category,
        tags: article.tags.join(', '),
        isFeatured: article.isFeatured,
        isBreakingNews: article.isBreakingNews,
        status: article.status as 'draft' | 'published',
        metaTitle: article.seo?.metaTitle || '',
        metaDescription: article.seo?.metaDescription || '',
        featuredImage: null,
      };

      setFormData(initialData);
      setOriginalData(initialData);
      setImagePreview(article.featuredImage.url);
    } catch (error: any) {
      console.error('❌ Failed to fetch article:', error);
      const errorMessage =
        error.response?.data?.message || 'Failed to load article. It may have been deleted.';
      toast.error(errorMessage);
      navigate('/top-news');
    } finally {
      setLoading(false);
    }
  };

  const modules = {
    toolbar: [
      [{ header: [1, 2, 3, 4, 5, 6, false] }],
      [{ font: [] }],
      [{ size: [] }],
      ['bold', 'italic', 'underline', 'strike', 'blockquote'],
      [{ list: 'ordered' }, { list: 'bullet' }, { indent: '-1' }, { indent: '+1' }],
      ['link', 'image', 'video'],
      [{ align: [] }],
      [{ color: [] }, { background: [] }],
      ['clean'],
    ],
  };

  const formats = [
    'header',
    'font',
    'size',
    'bold',
    'italic',
    'underline',
    'strike',
    'blockquote',
    'list',
    'bullet',
    'indent',
    'link',
    'image',
    'video',
    'align',
    'color',
    'background',
  ];

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value, type } = e.target;

    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData((prev) => ({ ...prev, [name]: checked }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleDescriptionChange = (value: string) => {
    setFormData((prev) => ({ ...prev, description: value }));
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file size (5MB max)
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Image size should be less than 5MB');
        return;
      }

      // Validate file type
      const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
      if (!validTypes.includes(file.type)) {
        toast.error('Only JPG, PNG, and WebP images are allowed');
        return;
      }

      setFormData((prev) => ({ ...prev, featuredImage: file }));

      // Generate preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);

      console.log('✅ New image selected:', file.name, `(${(file.size / 1024).toFixed(2)}KB)`);
    }
  };

  const removeImage = () => {
    setFormData((prev) => ({ ...prev, featuredImage: null }));
    setImagePreview('');
    console.log('🗑️ Image removed');
  };

  const hasChanges = (): boolean => {
    if (!originalData) return false;

    return (
      formData.headline !== originalData.headline ||
      formData.excerpt !== originalData.excerpt ||
      formData.description !== originalData.description ||
      formData.category !== originalData.category ||
      formData.tags !== originalData.tags ||
      formData.isFeatured !== originalData.isFeatured ||
      formData.isBreakingNews !== originalData.isBreakingNews ||
      formData.status !== originalData.status ||
      formData.metaTitle !== originalData.metaTitle ||
      formData.metaDescription !== originalData.metaDescription ||
      formData.featuredImage !== null
    );
  };

  const validateForm = (): boolean => {
    if (!formData.headline.trim()) {
      toast.error('Headline is required');
      return false;
    }

    if (formData.headline.length < 10) {
      toast.error('Headline must be at least 10 characters long');
      return false;
    }

    if (formData.headline.length > 200) {
      toast.error('Headline must not exceed 200 characters');
      return false;
    }

    if (!formData.description.trim() || formData.description === '<p><br></p>') {
      toast.error('Article content is required');
      return false;
    }

    if (formData.description.length < 100) {
      toast.error('Article content must be at least 100 characters long');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent, status: 'draft' | 'published') => {
    e.preventDefault();

    if (!validateForm()) return;

    if (!slug) {
      toast.error('Article slug is missing');
      return;
    }

    if (!hasChanges() && formData.status === status) {
      toast('No changes to save');
      return;
    }

    try {
      setSaving(true);
      console.log('💾 Updating article with slug:', slug);

      const submitData: UpdateArticleData = {
        headline: formData.headline.trim(),
        excerpt: formData.excerpt.trim() || formData.headline.substring(0, 150).trim(),
        description: formData.description,
        category: formData.category,
        tags: formData.tags
          .split(',')
          .map((tag) => tag.trim())
          .filter(Boolean),
        status,
        isFeatured: formData.isFeatured,
        isBreakingNews: formData.isBreakingNews,
        metaTitle: formData.metaTitle.trim() || formData.headline.trim(),
        metaDescription:
          formData.metaDescription.trim() || formData.excerpt.trim() || formData.headline.substring(0, 160).trim(),
      };

      // Only add image if a new one was selected
      if (formData.featuredImage) {
        submitData.featuredImage = formData.featuredImage;
        console.log('📷 New featured image will be uploaded');
      }

      console.log('📤 Submitting update data:', {
        headline: submitData.headline,
        category: submitData.category,
        status: submitData.status,
        tagsCount: submitData.tags?.length || 0,
        hasNewImage: !!submitData.featuredImage,
      });

      const updatedArticle = await articleService.updateArticle(slug, submitData);

      console.log('✅ Article updated successfully:', updatedArticle.slug);
      toast.success(
        `Article ${status === 'published' ? 'published' : 'updated'} successfully!`
      );

      navigate('/top-news');
    } catch (error: any) {
      console.error('❌ Failed to update article:', error);

      let errorMessage = 'Failed to update article';

      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.response?.data?.errors) {
        const errors = error.response.data.errors;
        errorMessage = Object.values(errors).join(', ');
      } else if (error.message) {
        errorMessage = error.message;
      }

      toast.error(errorMessage);

      if (error.response?.data) {
        console.error('Error details:', error.response.data);
      }
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (hasChanges()) {
      const confirmed = window.confirm(
        'You have unsaved changes. Are you sure you want to leave?'
      );
      if (!confirmed) return;
    }
    navigate('/top-news');
  };

  if (loading) {
    return (
      <div className={styles.loaderContainer}>
        <Loader size="large" text="Loading article..." />
      </div>
    );
  }

  return (
    <div className={styles.createArticle}>
      <div className={styles.header}>
        <Button variant="ghost" icon={<FiArrowLeft />} onClick={handleCancel}>
          Back to Articles
        </Button>
        <h1 className={styles.title}>Edit Article</h1>
        <p className={styles.subtitle}>Update article details and content</p>
      </div>

      <form className={styles.form}>
        {/* Featured Image Upload */}
        <Card title="Featured Image" className={styles.section}>
          <div className={styles.imageUpload}>
            {imagePreview ? (
              <div className={styles.imagePreviewContainer}>
                <img src={imagePreview} alt="Preview" className={styles.imagePreview} />
                <div className={styles.imageActions}>
                  <label className={styles.changeImage}>
                    <FiImage /> Change Image
                    <input
                      type="file"
                      accept="image/jpeg,image/jpg,image/png,image/webp"
                      onChange={handleImageChange}
                      className={styles.fileInput}
                    />
                  </label>
                  {formData.featuredImage && (
                    <button type="button" className={styles.removeImage} onClick={removeImage}>
                      <FiX /> Remove New Image
                    </button>
                  )}
                </div>
              </div>
            ) : (
              <label className={styles.imageUploadLabel}>
                <FiImage className={styles.uploadIcon} />
                <p className={styles.uploadText}>Click to upload new featured image</p>
                <p className={styles.uploadHint}>PNG, JPG, WebP up to 5MB</p>
                <input
                  type="file"
                  accept="image/jpeg,image/jpg,image/png,image/webp"
                  onChange={handleImageChange}
                  className={styles.fileInput}
                />
              </label>
            )}
          </div>
        </Card>

        {/* Article Details */}
        <Card title="Article Details" className={styles.section}>
          <div className={styles.formGrid}>
            <div className={styles.fullWidth}>
              <Input
                name="headline"
                label="Headline *"
                placeholder="Enter article headline (10-200 characters)"
                value={formData.headline}
                onChange={handleInputChange}
                required
                fullWidth
                maxLength={200}
              />
              <p className={styles.charCount}>{formData.headline.length}/200 characters</p>
            </div>

            <div className={styles.fullWidth}>
              <label className={styles.label}>Excerpt</label>
              <textarea
                name="excerpt"
                placeholder="Brief summary of the article..."
                value={formData.excerpt}
                onChange={handleInputChange}
                className={styles.textarea}
                rows={3}
                maxLength={300}
              />
              <p className={styles.charCount}>{formData.excerpt.length}/300 characters</p>
            </div>

            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label className={styles.label}>Category *</label>
                <select
                  name="category"
                  value={formData.category}
                  onChange={handleInputChange}
                  className={styles.select}
                  required
                >
                  <option value="tech">Technology</option>
                  <option value="innovation">Innovation</option>
                  <option value="unicorn">Unicorn</option>
                  <option value="trending">Trending</option>
                  <option value="featured">Featured</option>
                  <option value="women-empowerment">Women Empowerment</option>
                  <option value="farming">Farming</option>
                  <option value="case-study">Case Study</option>
                  <option value="early-birds-startup">Early Birds Startup</option>
                  <option value="recognition">Recognition</option>
                  <option value="startuprahi">StartupRahi</option>
                  <option value="manufacturing">Manufacturing</option>
                  <option value="stock-market">Stock Market</option>
                  <option value="marketing">Marketing</option>
                  <option value="stories">Stories</option>
                </select>
              </div>

              <div className={styles.formGroup}>
                <Input
                  name="tags"
                  label="Tags (comma separated)"
                  placeholder="ai, startup, technology"
                  value={formData.tags}
                  onChange={handleInputChange}
                  fullWidth
                />
              </div>
            </div>

            <div className={styles.checkboxGroup}>
              <label className={styles.checkbox}>
                <input
                  type="checkbox"
                  name="isFeatured"
                  checked={formData.isFeatured}
                  onChange={handleInputChange}
                />
                <span>⭐ Mark as Featured</span>
              </label>

              <label className={styles.checkbox}>
                <input
                  type="checkbox"
                  name="isBreakingNews"
                  checked={formData.isBreakingNews}
                  onChange={handleInputChange}
                />
                <span>🔥 Mark as Breaking News</span>
              </label>
            </div>
          </div>
        </Card>

        {/* Article Content (Editor) */}
        <Card title="Article Content *" className={styles.section}>
          <div className={styles.editorContainer}>
            <ReactQuill
              theme="snow"
              value={formData.description}
              onChange={handleDescriptionChange}
              modules={modules}
              formats={formats}
              placeholder="Write your article content here..."
              className={styles.editor}
            />
          </div>
        </Card>

        {/* SEO Settings */}
        <Card title="SEO Settings (Optional)" className={styles.section}>
          <div className={styles.formGrid}>
            <div className={styles.fullWidth}>
              <Input
                name="metaTitle"
                label="Meta Title"
                placeholder="SEO title"
                value={formData.metaTitle}
                onChange={handleInputChange}
                fullWidth
                maxLength={60}
              />
              <p className={styles.charCount}>{formData.metaTitle.length}/60 characters</p>
            </div>

            <div className={styles.fullWidth}>
              <label className={styles.label}>Meta Description</label>
              <textarea
                name="metaDescription"
                placeholder="SEO description..."
                value={formData.metaDescription}
                onChange={handleInputChange}
                className={styles.textarea}
                rows={2}
                maxLength={160}
              />
              <p className={styles.charCount}>{formData.metaDescription.length}/160 characters</p>
            </div>
          </div>
        </Card>

        {/* Action Buttons */}
        <div className={styles.actions}>
          <Button type="button" variant="ghost" onClick={handleCancel} disabled={saving}>
            Cancel
          </Button>

          <div className={styles.submitButtons}>
            <Button
              type="button"
              variant="secondary"
              onClick={(e) => handleSubmit(e, 'draft')}
              loading={saving}
              disabled={saving || !hasChanges()}
            >
              Save as Draft
            </Button>

            <Button
              type="button"
              variant="primary"
              icon={<FiSave />}
              onClick={(e) => handleSubmit(e, 'published')}
              loading={saving}
              disabled={saving}
            >
              Update & Publish
            </Button>
          </div>
        </div>

        {hasChanges() && (
          <div className={styles.unsavedChanges}>
            ⚠️ You have unsaved changes
          </div>
        )}
      </form>
    </div>
  );
};

export default EditArticle;